import { getImageProps } from "next/image";
import { preload } from "react-dom";
import { STEP_IMAGE_URL_PROPS, type StepImageEntry } from "./QuizStepImage";

/**
 * Warm the exact optimizer variant <Image> will request for a step image.
 *
 * Derives src/srcSet via Next's own getImageProps using the same
 * STEP_IMAGE_URL_PROPS QuizStepImageCarousel spreads onto <Image>, so the
 * candidate widths and the browser's final pick are identical to what the
 * real <Image> resolves to. This also makes React's preload dedupe key
 * (imagesrcset+imagesizes) match next/image's own internal priority preload
 * — a hand-built `?w=&q=` URL carries only an href, which next/image's
 * srcSet-keyed preload can never dedupe against, so it silently
 * double-fetches instead of reusing the warm cache entry.
 *
 * Takes the resolved image directly (not a step/index pair) — each step's
 * carousel order is computed once up front (see useQuizWizardController.ts),
 * so the image that will actually show first is already known by the time
 * this is called.
 */
export function preloadStepImage(
  image: StepImageEntry,
  fetchPriority: "high" | "low" | "auto" = "auto",
): void {
  const { props } = getImageProps({
    src: image.src,
    alt: "",
    ...STEP_IMAGE_URL_PROPS,
  });
  preload(props.src, {
    as: "image",
    ...(props.srcSet ? { imageSrcSet: props.srcSet } : {}),
    ...(props.sizes ? { imageSizes: props.sizes } : {}),
    fetchPriority,
  });
}
